import {
  InventoryProvider,
  MeasurementQuery,
  MeasurementRange,
  MeasurementRangeQuery,
  PaginatedDictResponse,
  SitecontrolMeasurement,
  WelfareScoreEntry,
} from '@scaleaq/sitecontrol-lib';

import { PartialBy } from '@/types.ts';

export type StructuredMeasurements<T extends SitecontrolMeasurement | MeasurementRange> = {
  [plotType: string]: {
    [inventoryId: string]: T[];
  };
};

/** Per-series query status for a single plot type + inventory, from `useGetMeasurementRanges`. */
type MeasurementSeriesStatus = {
  isError: boolean;
  isFetched: boolean;
  isLoading: boolean;
  isRateLimited: boolean;
};

/**
 * Status keyed like `StructuredMeasurements`: plot type → inventory id.
 * Failed or in-flight series still appear here even when they are absent from `data`.
 */
export type MeasurementSeriesStatusMap = {
  [plotType: string]: {
    [inventoryId: string]: MeasurementSeriesStatus;
  };
};

// make properties with default values optional
export type PaginatedMeasurementRequestParams = PartialBy<
  MeasurementQuery,
  'page' | 'pageSize' | 'includeCount' | 'includeSubSensors' | 'mergeSeries'
>;

// make properties with default values optional
export type MeasurementRangesRequestParams = PartialBy<
  MeasurementRangeQuery,
  'includeSubSensors' | 'mergeSeries'
>;

// Items must be omitted and redefined due to typeGen erroneously translating the dictionary to a list of key value pairs rather than a record
export interface KeyedPaginatedResponse<
  T extends SitecontrolMeasurement | MeasurementRange | WelfareScoreEntry,
> extends Omit<PaginatedDictResponse<string, T[]>, 'items'> {
  type: string;
  items: Record<string, T[]>;
}

/** Single-inventory measurement/ranges response (`items` keyed by inventory id). */
export type MeasurementRangeSubSensorResponse = Omit<
  PaginatedDictResponse<string, MeasurementRange[]>,
  'items'
> & {
  items: Record<string, MeasurementRange[]>;
};

export enum WindowSize {
  OneDay = '1 day',
  ThreeDays = '3 days',
  SevenDays = '1 week',
}

export const WelfareProviders = [
  InventoryProvider.WoundsActive,
  InventoryProvider.WoundsHealed,
  InventoryProvider.SnoutDamage,
  InventoryProvider.ScaleLoss,
] as const;

export type WelfareProvider = (typeof WelfareProviders)[number];
